import _ from 'lodash';

import {
  useCallback,
  useMemo
} from 'react';
import {
  ImportShipItemInput,
  useImportNetSuiteShipItemsModalQuery,
  useImportNetSuiteShipItemsMutation
} from 'generated/graphql';

import { ImportCsvModal } from 'iron-forge-react-modules';
import * as types from './types';



export type IColumnDef = ImportCsvModal.IColumnDescription<types.NetSuiteShipItemsPropertyName>;

export interface INetSuiteSubsidiary {
  id: string;
  name: string;
}

export interface IData {
  propMap: INetSuiteSubsidiary[];
  columnDefs: IColumnDef[];
}



export function useQuery() {
  const tuple = useImportNetSuiteShipItemsModalQuery({
    variables: {}
  });

  return useMemo(
    () => {
      const data: IData = {
        propMap: tuple.data?.subsidiaries ?? [],
        columnDefs: [
          {
            id: 'Internal ID',
            label: 'Internal ID',
            isRequired: true,
            formatType: undefined
          },
          {
            id: 'Name',
            label: 'Name',
            isRequired: true,
            formatType: undefined
          },
          {
            id: 'Display Code',
            label: 'Display Code',
            isRequired: false,
            formatType: undefined
          },
          {
            id: 'Description',
            label: 'Description',
            isRequired: false,
            formatType: undefined
          },
          {
            id: 'Subsidiary',
            label: 'Subsidiary',
            isRequired: true,
            formatType: undefined
          }
        ]
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}



export function useImportNetSuiteShipItemsFromCsv() {
  const [fnMutate, tuple] = useImportNetSuiteShipItemsMutation({
    refetchQueries: []
  });

  const fnWrappedMutate = useCallback(
    (rows: Array<_.Dictionary<string>>, data: IData) => {
      const input = __formatTransform({
        rawCsvRows: rows,
        data
      });

      fnMutate({
        variables: {
          input
        }
      });
    },
    [fnMutate]
  );

  return useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}



function __formatTransform({
  rawCsvRows, data
}: {
  rawCsvRows: Array<_.Dictionary<string>>;
  data: IData;
}): ImportShipItemInput[] {
  const subsidiaries = data.propMap;

  if (_.isEmpty(subsidiaries)) {
    throw new Error("Error fetching subsidiaries data");
  }

  const subsidiaryMap = _.reduce(subsidiaries, (map, subsidiary) => {
    map[subsidiary.name] = subsidiary.id;
    return map;
  }, {} as Record<string, string>);

  const rowsAsPropertyName2Value: ImportShipItemInput[] = _.map(rawCsvRows, row => {
    return {
      netSuiteInternalId: row['Internal ID'],
      name: row['Name'],
      subsidiaryId: subsidiaryMap[row['Subsidiary']] || ''
    };
  });

  return rowsAsPropertyName2Value;
}
